/**
 * @module UserManagementPage
 *
 * ユーザー管理画面コンポーネント
 * - ユーザー一覧テーブル（名前、メール、ロール、ステータス、最終ログイン）
 * - ユーザー追加ダイアログ（名前、メール、パスワード、ロール選択）
 * - ユーザー編集ダイアログ（パスワードは任意）
 * - アカウントロック/解除ボタン
 *
 * Requirements: 12.1, 12.2
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ConfirmDialog from '../components/ConfirmDialog';

// ============================================================
// 型定義
// ============================================================

interface UserItem {
  id: string;
  name: string;
  email: string;
  role: string;
  isLocked: boolean;
  lastLoginAt: string | null;
  customerId?: string | null;
}

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: string;
  customerId: string;
}

// ============================================================
// 定数
// ============================================================

const ROLE_LABELS: Record<string, string> = {
  admin: '管理者',
  general: '一般ユーザー',
  viewer: '閲覧専用',
  customer: '顧客',
};

const INITIAL_FORM_DATA: UserFormData = {
  name: '',
  email: '',
  password: '',
  role: 'general',
  customerId: '',
};

// ============================================================
// サブコンポーネント
// ============================================================

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UserFormData) => void;
  title: string;
  initialData?: UserFormData;
  isEdit?: boolean;
  isSubmitting?: boolean;
  error?: string | null;
}

const UserDialog: React.FC<UserDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  initialData = INITIAL_FORM_DATA,
  isEdit = false,
  isSubmitting = false,
  error = null,
}) => {
  const [formData, setFormData] = useState<UserFormData>(initialData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // 顧客一覧（ロール「顧客」選択時のドロップダウン用）
  const { data: customersData } = useQuery<{ data: { id: string; customerCode: string; name: string }[] }>({
    queryKey: ['customers-list-for-user'],
    queryFn: async () => { const res = await api.get('/customers'); return res.data; },
    enabled: open,
  });
  const customers = customersData?.data ?? [];

  React.useEffect(() => {
    if (open) {
      setFormData(initialData);
      setFormErrors({});
    }
  }, [open, initialData]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = '名前は必須です';
    }
    if (!formData.email.trim()) {
      errors.email = 'メールアドレスは必須です';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'メールアドレスの形式が正しくありません';
    }
    if (!isEdit && !formData.password) {
      errors.password = 'パスワードは必須です';
    } else if (formData.password && formData.password.length < 8) {
      errors.password = 'パスワードは8文字以上で入力してください';
    }
    if (!formData.role) {
      errors.role = 'ロールは必須です';
    }
    if (formData.role === 'customer' && !formData.customerId) {
      errors.customerId = '顧客コードは必須です';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="名前"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!formErrors.name}
            helperText={formErrors.name}
          />
          <TextField
            margin="dense"
            label="メールアドレス"
            type="email"
            fullWidth
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!formErrors.email}
            helperText={formErrors.email}
          />
          <TextField
            margin="dense"
            label={isEdit ? 'パスワード（変更する場合のみ）' : 'パスワード'}
            type="password"
            fullWidth
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            error={!!formErrors.password}
            helperText={formErrors.password}
            autoComplete="new-password"
          />
          <FormControl fullWidth margin="dense" error={!!formErrors.role}>
            <InputLabel id="role-select-label">ロール</InputLabel>
            <Select
              labelId="role-select-label"
              label="ロール"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="admin">管理者</MenuItem>
              <MenuItem value="general">一般ユーザー</MenuItem>
              <MenuItem value="viewer">閲覧専用</MenuItem>
              <MenuItem value="customer">顧客</MenuItem>
            </Select>
          </FormControl>
          {formData.role === 'customer' && (
            <FormControl fullWidth margin="dense" error={!!formErrors.customerId}>
              <InputLabel id="customer-select-label">顧客コード</InputLabel>
              <Select
                labelId="customer-select-label"
                label="顧客コード"
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
              >
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.customerCode} {c.name}</MenuItem>
                ))}
              </Select>
              {formErrors.customerId && (
                <Typography variant="caption" color="error">{formErrors.customerId}</Typography>
              )}
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ============================================================
// メインコンポーネント
// ============================================================

const UserManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserItem | null>(null);

  // ユーザー一覧取得
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery<{ data: UserItem[] }>({
    queryKey: ['users-management'],
    queryFn: async () => {
      const res = await api.get('/users');
      return res.data;
    },
  });

  // ユーザー作成
  const createMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const res = await api.post('/users', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-management'] });
      setAddDialogOpen(false);
      setDialogError(null);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setDialogError(axiosErr.response?.data?.message ?? 'ユーザーの作成に失敗しました');
    },
  });

  // ユーザー更新
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<UserFormData> }) => {
      const res = await api.put(`/users/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-management'] });
      setEditDialogOpen(false);
      setEditingUser(null);
      setDialogError(null);
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setDialogError(axiosErr.response?.data?.message ?? 'ユーザーの更新に失敗しました');
    },
  });

  // ユーザー削除（論理削除）
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/users/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-management'] });
      setDeleteTarget(null);
    },
  });

  // アカウントロック解除
  const unlockMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/users/${id}/unlock`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users-management'] });
    },
  });

  // ハンドラー
  const handleAddUser = (data: UserFormData) => {
    setDialogError(null);
    createMutation.mutate(data);
  };

  const handleEditUser = (data: UserFormData) => {
    if (!editingUser) return;
    setDialogError(null);
    const updateData: Record<string, unknown> = {
      name: data.name,
      email: data.email,
      role: data.role,
    };
    if (data.password) {
      updateData.password = data.password;
    }
    // Only send customerId if role is customer (with value) or changed away from customer (clear it)
    if (data.role === 'customer') {
      updateData.customerId = data.customerId || null;
    } else if (editingUser.role === 'customer') {
      // Was customer, now changed to another role - clear customerId
      updateData.customerId = null;
    }
    updateMutation.mutate({ id: editingUser.id, data: updateData as Partial<UserFormData> });
  };

  const handleOpenEdit = (user: UserItem) => {
    setEditingUser(user);
    setDialogError(null);
    setEditDialogOpen(true);
  };

  const handleUnlock = (user: UserItem) => {
    unlockMutation.mutate(user.id);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ローディング状態
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // エラー状態
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        ユーザーデータの取得に失敗しました。
      </Alert>
    );
  }

  const users = usersData?.data ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px - 48px)', overflow: 'hidden' }}>
      {/* Fixed header portion */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            ユーザー管理
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setDialogError(null);
              setAddDialogOpen(true);
            }}
          >
            ユーザー追加
          </Button>
        </Box>
      </Box>

      {/* Scrollable table portion */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {users.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 2 }}>
              ユーザーが登録されていません。
            </Typography>
          ) : (
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>名前</TableCell>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>ロール</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>最終ログイン</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={ROLE_LABELS[user.role] ?? user.role}
                          size="small"
                          color={user.role === 'admin' ? 'primary' : 'default'}
                          variant={user.role === 'admin' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {user.isLocked ? (
                          <Chip label="ロック中" color="error" size="small" />
                        ) : (
                          <Chip label="アクティブ" color="success" size="small" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="編集">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEdit(user)}
                            aria-label={`${user.name}を編集`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.isLocked ? (
                          <Tooltip title="ロック解除">
                            <IconButton
                              size="small"
                              onClick={() => handleUnlock(user)}
                              aria-label={`${user.name}のロックを解除`}
                              color="warning"
                            >
                              <LockOpenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="ロック中ではありません">
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                aria-label={`${user.name}はロックされていません`}
                              >
                                <LockIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {/* 自分自身は削除不可 */}
                        <Tooltip title={user.id === currentUser?.id ? '自分自身は削除できません' : '削除'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setDeleteTarget(user)}
                              disabled={user.id === currentUser?.id}
                              aria-label={`${user.name}を削除`}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* ユーザー追加ダイアログ */}
      <UserDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddUser}
        title="ユーザー追加"
        isSubmitting={createMutation.isPending}
        error={dialogError}
      />

      {/* ユーザー編集ダイアログ */}
      <UserDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingUser(null);
        }}
        onSubmit={handleEditUser}
        title="ユーザー編集"
        initialData={
          editingUser
            ? { name: editingUser.name, email: editingUser.email, password: '', role: editingUser.role, customerId: (editingUser as any).customerId ?? '' }
            : INITIAL_FORM_DATA
        }
        isEdit
        isSubmitting={updateMutation.isPending}
        error={dialogError}
      />

      {/* ユーザー削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="ユーザーの削除"
        message={`「${deleteTarget?.name}」（${deleteTarget?.email}）を削除しますか？この操作は取り消せません。`}
        confirmLabel="削除"
        cancelLabel="キャンセル"
        severity="error"
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default UserManagementPage;
